import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { CodeVerificationErrorResponse, CodeVerificationResponse } from '../types/api.types';

import { verifyEventCodeService } from 'services/referral-event.service';

import { REFERRAL_CACHE_KEYS } from '../constants';

type UseCodeVerificationOptions = {
  eventId: string;
  onSuccess?: (response: CodeVerificationResponse) => void;
  onError?: (message: string, status?: string, reasons?: string[]) => void;
};

type VerifyCodeParams = {
  code: string;
};

export const useCodeVerification = ({ eventId, onSuccess, onError }: UseCodeVerificationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ code }: VerifyCodeParams) => verifyEventCodeService(eventId, code),
    onSuccess: (response) => {
      // Invalidate user metrics and user status queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_METRICS, eventId] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_STATUS, eventId] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.REFERRAL_EVENT, eventId] });
      onSuccess?.(response.data);
    },
    onError: (error: AxiosError<CodeVerificationErrorResponse>) => {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || 'Code verification failed. Please try again.';
      const errorStatus = errorData?.status;
      const errorReasons = errorData?.reasons;
      onError?.(errorMessage, errorStatus, errorReasons);
    },
  });
};
